import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Row, Space, Form, message, UploadProps, UploadFile, Upload } from 'antd';
import { BankOutlined, PrinterOutlined } from '@ant-design/icons';
import OrderProductItems from './createUpdate/OrderProductItems';
import OrderCustomerForm from './createUpdate/OrderCustomerForm';
import './Order.styles.css';
import moment from 'moment';
import { OrderModel } from '@app/interfaces/order';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { addProductItems, createOrder, updateOrder } from '@app/store/slices/orderSlice';
import { getDraft, saveDraft, removeTab } from '@app/services/orderDraft.service';
import { useNavigate } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import PrintOrderTemp from './detail/PrintOrderTemp';
import { useAppSetting } from '@app/contexts/AppSettingContext';
import { EmployeeOption, DEPARTMENT_DESIGNER, DEPARTMENT_SHIPPER } from '@app/interfaces/employee';
import { MdCancel } from 'react-icons/md';
import { readUser } from '@app/services/localStorage.service';
import { getListEmployees } from '@app/api/order.api';
import { getBanksApi, getBankShopQRCode } from '@app/api/bank.api';
import { getOnePayQRBase64 } from '@app/api/onepay.api';
import { StoreModel, StoreOption } from '@app/interfaces/store';
import { getStores } from '@app/store/slices/storeSlice';
import { printHtmlBody } from '@app/utils/printDocument';

interface OrderFormProps {
  tabKey: string;
  record?: OrderModel | null;
}

interface CustomerState {
  id?: number;
  name?: string;
  address?: string;
}

const OrderForm: React.FC<OrderFormProps> = ({ tabKey, record }) => {
  const { logoUrl, storeName, storePhone, storeAddress, storeWebsite, onepayEnable } = useAppSetting();
  const [form] = Form.useForm();
  const calTotalAmountRef = useRef<(() => void) | null>(null);
  const [customer, setCustomer] = useState<CustomerState>({});
  const [sellerOptions, setSellerOptions] = useState<EmployeeOption[]>([]);
  const [shipperOptions, setShipperOptions] = useState<EmployeeOption[]>([]);
  const [designerOptions, setDesignerOptions] = useState<EmployeeOption[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [totalAmountTemp, setTotalAmountTemp] = useState(0);
  const [store, setStore] = useState<StoreModel | null>(null);
  const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const currentUser = readUser();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Đơn tạm (draft) chỉ áp dụng khi tạo đơn MỚI (không dùng cho màn sửa đơn).
  const isDraftMode = !record;
  // Đọc đơn tạm MỘT lần (getDraft parse cả localStorage). Khách chọn sẵn chỉ áp dụng khi form
  // đã lưu chưa chọn khách khác; đã có địa chỉ giao trong đơn tạm thì không tự điền đè.
  const [draftPrefill] = useState(() => {
    if (!isDraftMode) return { customer: null, keepAddress: false };
    const draft = getDraft(tabKey);
    const savedId = draft?.formValues?.customer_id;
    const customer = draft?.customer && (!savedId || savedId === draft.customer.id) ? draft.customer : null;
    return { customer, keepAddress: Boolean(draft?.formValues?.delivery_address) };
  });
  const productItems = useAppSelector((state) => state.order.productItems);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Chuyển moment -> ISO để lưu JSON; loại bỏ field không cần.
  const persistDraft = () => {
    if (!isDraftMode) return;
    const fv: Record<string, unknown> = { ...form.getFieldsValue(true) };
    if (moment.isMoment(fv.trans_date)) fv.trans_date = (fv.trans_date as moment.Moment).toISOString();
    if (moment.isMoment(fv.delivery_date)) fv.delivery_date = (fv.delivery_date as moment.Moment).toISOString();
    delete fv.items; // sản phẩm lưu riêng qua productItems
    saveDraft(tabKey, { formValues: fv, productItems });
  };

  // Ref giữ bản persistDraft mới nhất để flush khi unmount (đổi tab) không bị stale.
  const persistRef = useRef(persistDraft);
  persistRef.current = persistDraft;

  const scheduleSaveDraft = () => {
    if (!isDraftMode) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistRef.current(), 400);
  };

  useEffect(() => {
    fetchEmployees();
    fetchStores();
  }, []);

  // Nạp draft đã lưu cho tab này (chỉ đơn mới).
  useEffect(() => {
    if (!isDraftMode) return;
    const draft = getDraft(tabKey);
    if (draft && Object.keys(draft.formValues || {}).length > 0) {
      const fv: Record<string, unknown> = { ...draft.formValues };
      if (fv.trans_date) fv.trans_date = moment(fv.trans_date as string);
      if (fv.delivery_date) fv.delivery_date = moment(fv.delivery_date as string);
      form.setFieldsValue(fv);
    }
    dispatch(addProductItems(draft?.productItems ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabKey]);

  // Lưu draft khi danh sách sản phẩm đổi; flush lần cuối khi rời tab.
  useEffect(() => {
    scheduleSaveDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productItems]);

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      persistRef.current();
    },
    [],
  );

  useEffect(() => {
    if (record) {
      // Convert the date field properly
      const formValues = {
        ...record,
        trans_date: record.trans_date ? moment(record.trans_date) : moment(),
        delivery_date: record.delivery_date ? moment(record.delivery_date) : moment(),
        designer_ids: record.designer_ids ?? [],
        co_seller_ids: record.co_seller_ids ?? [],
        points_requested: record.points_used ?? 0,
      };
      form.setFieldsValue(formValues);

      dispatch(addProductItems(record.items));
    }
  }, [record]);

  useEffect(() => {
    // Chỉ điền địa chỉ khi thực sự chọn khách hàng (tránh ghi đè địa chỉ đã khôi phục từ draft).
    if (customer?.address) {
      form.setFieldsValue({ delivery_address: customer.address });
    } else if (record?.delivery_address) {
      form.setFieldsValue({ delivery_address: record.delivery_address });
    }
  }, [customer, form, record]);

  useEffect(() => {
    fetchBanks();
  }, [record, customer, totalAmountTemp]);

  const fetchBanks = async () => {
    try {
      const newParams = { bank_type_eq: 0, active_true: true }; //type is => shop
      const response = await getBanksApi(newParams);
      const data = response?.rows.map((item: any) => item.attributes)[0] || {};
      const qrCode = await getBankShopQRCode(data, totalAmountTemp, `Chuyen khoan`);

      if (qrCode) {
        setQrCodeUrl(qrCode);
      }
    } catch {
      // QR chỉ để in kèm bill — không tải được thì vẫn tạo đơn bình thường.
    }
  };

  const fetchStores = async () => {
    try {
      const response = await dispatch(getStores());
      const data = response.payload as StoreModel[];
      if (data && data?.length > 0) {
        setStore(data[0]);

        const options = data.map((item: any) => ({ value: item.id, label: item.name }));
        setStoreOptions(options);

        // Chỉ set cửa hàng mặc định khi chưa có (giữ giá trị đã khôi phục từ draft)
        if (!record && data[0].id && !form.getFieldValue('store_id')) {
          form.setFieldsValue({ store_id: data[0].id });
        }
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleSubmit = (action: string) => {
    if (isSubmitting || isPrinting) return; // chặn double-click tạo đơn trùng
    form
      .validateFields()
      .then((values) => {
        //form.resetFields();
        action === 'create_update' ? onCreate(values) : handlePrint(values, customer);
      })
      .catch((info) => {
        message.error('Validate failed:', info);
      });
  };

  const onCreate = async (values: any) => {
    console.log(values);
    setIsSubmitting(true);

    try {
      let response = null;

      if (record && record.id) {
        response = await dispatch(updateOrder({ orderId: Number(record.id), formData: values })).unwrap();
        message.success('Cập nhật đơn hàng thành công!');
      } else {
        const formData = new FormData();

        Object.keys(values).forEach((key) => {
          if (key === 'items' || key === 'designer_ids' || key === 'co_seller_ids') return;
          if (values[key] !== undefined && values[key] !== null) {
            formData.append(key, values[key]);
          }
        });

        // Mảng id gửi dạng key[] để Rails nhận đúng (designer_ids, co_seller_ids).
        ['designer_ids', 'co_seller_ids'].forEach((arrayKey) => {
          if (Array.isArray(values[arrayKey])) {
            values[arrayKey].forEach((id: number) => {
              formData.append(`${arrayKey}[]`, String(id));
            });
          }
        });

        formData.append('remark', values.remark || '');

        if (values.items) {
          formData.append('items', JSON.stringify(values.items));
        }

        if (fileList && fileList.length > 0) {
          fileList.forEach((file: any, index: number) => {
            if (file.originFileObj) {
              formData.append(`order_assets_attributes[${index}][asset_type]`, 'confirmed');
              formData.append(`order_assets_attributes[${index}][image]`, file.originFileObj);
              formData.append(`order_assets_attributes[${index}][name]`, file.name);
              formData.append(`order_assets_attributes[${index}][content_type]`, file.type);
              formData.append(`order_assets_attributes[${index}][size]`, file.size.toString());
              //formData.append(`order_assets_attributes[${index}][url]`, file.thumbUrl); // Store the thumbUrl
              formData.append(`order_assets_attributes[${index}[created_by]`, currentUser?.name || '');
              formData.append(`order_assets_attributes[${index}[_destroy]`, 'false');
            }
          });
        }

        response = await dispatch(createOrder(formData)).unwrap();
        message.success('Tạo đơn hàng thành công!');
      }

      if (response && response.data && response.data.id) {
        // Đơn đã tạo thành công -> xoá draft của tab này để không khôi phục lại.
        if (isDraftMode) {
          if (saveTimer.current) clearTimeout(saveTimer.current);
          removeTab(tabKey);
        }
        setTimeout(() => {
          navigate(`/orders/${response.data.id}`);
        }, 500);
      } else {
        throw new Error('Tạo đơn hàng thất bại. Vui lòng thử lại.');
      }
    } catch (error: any) {
      message.error(error.message || 'Có lỗi xảy ra khi xử lý đơn hàng');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrint = async (values: any, customer: any) => {
    setIsPrinting(true);
    try {
      // Lưu trước khi in: QR OnePay được backend tính từ DB, nên in giá trị form
      // chưa lưu sẽ làm giấy và QR lệch số tiền (regression HD02209).
      if (record && record.id) {
        await dispatch(updateOrder({ orderId: Number(record.id), formData: values })).unwrap();
      }

      const absoluteLogoUrl = logoUrl.startsWith('http') ? logoUrl : `${window.location.origin}${logoUrl}`;

      let resolvedQr = qrCodeUrl;
      let qrType: 'vietqr' | 'onepay' = 'vietqr';
      if (onepayEnable) {
        if (!record?.id) {
          message.info('Lưu đơn hàng trước để hiển thị mã QR OnePay. Đang dùng VietQR tạm thời.');
        } else {
          const onePayQr = await getOnePayQRBase64('Order', Number(record.id));
          if (onePayQr) {
            resolvedQr = onePayQr;
            qrType = 'onepay';
          } else {
            message.warning('Không thể tải QR OnePay, đang dùng VietQR thay thế.');
          }
        }
      }

      const printContent = ReactDOMServer.renderToString(
        <PrintOrderTemp
          store={store}
          record={values}
          customer={customer}
          qrCodeUrl={resolvedQr}
          qrType={qrType}
          logoUrl={absoluteLogoUrl}
          storeName={storeName}
          storeAddress={storeAddress}
          storePhone={storePhone}
          storeWebsite={storeWebsite}
        />,
      );

      await printHtmlBody(printContent, {
        title: 'In đơn hàng tạm',
        headHtml: `
          <style>
            .logo-company {
              width: 150px !important;
              height: auto !important;
              display: block;
              align-self: flex-start;
              margin-top: -8px;
              border: none;
              outline: none;
            }
          </style>
        `,
      });

      message.success('Đã mở hộp thoại in.');
    } catch (error) {
      console.error('[PrintOrderTemp] renderToString error:', error);
      message.error('Có lỗi xảy ra khi in đơn hàng');
    } finally {
      setTimeout(() => setIsPrinting(false), 1000);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await getListEmployees();

      const groupedData = response?.reduce(
        (acc: any, item: any) => {
          const { department } = item;
          if (department === DEPARTMENT_SHIPPER) {
            acc.shipper.push(item);
          } else if (department === DEPARTMENT_DESIGNER) {
            acc.designer.push(item);
          } else {
            acc.seller.push(item);
          }
          return acc;
        },
        { shipper: [], designer: [], seller: [] }, // Initial accumulator
      );

      // Map each group to options
      const mapToOptions = (data: any) =>
        data?.map((item: any) => ({
          value: item.id,
          label: item.name,
          avatar_url: item.avatar_url,
          employee_code: item.employee_code,
          department: item.department,
        }));

      const additionaOptions = [{ value: 0, label: 'Khác', avatar_url: null, employee_code: null, department: null }];

      const mergedDesignerOptions = [...mapToOptions(groupedData.designer), ...additionaOptions];

      const mergedShipperOptions = [...mapToOptions(groupedData.shipper), ...additionaOptions];

      console.log('Merged Seller Options:', mergedDesignerOptions);
      console.log('Merged Seller Options:', mergedShipperOptions);

      setSellerOptions(mapToOptions(groupedData.seller));
      setShipperOptions(mergedShipperOptions);
      setDesignerOptions(mergedDesignerOptions);
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const props: UploadProps = {
    name: 'documents',
    multiple: true,
    fileList: fileList,
    accept: 'image/*',
    listType: 'picture-card',
    beforeUpload: (file) => {
      // Check file type
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error(`${file.name} is not an image file`);
        return Upload.LIST_IGNORE;
      }

      // Check file size (optional, set to 5MB here)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB!');
        return Upload.LIST_IGNORE;
      }

      return false; // Prevent auto upload
    },
    onChange(info) {
      setFileList(info.fileList);

      if (info.file.status === 'removed') {
        setFileList(info.fileList);
      }
    },
    onPreview: async (file) => {
      // Handle preview click
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj as File);
      }

      // Open preview in new window
      const image = file.url || file.preview;
      const newWindow = window.open('');
      if (newWindow) {
        newWindow.document.write(`<img src="${image}" alt="${file.name}" style="max-width: 100%; height: auto;" />`);
      }
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
  };

  // Helper function to convert file to base64 for preview
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Form
      form={form}
      name="form-order"
      initialValues={{
        //seller: user?.name,
        trans_date: moment(),
        order_channel_id: 1,
        payment_type: 'bank_transfer', // mặc định Chuyển khoản (CK) khi tạo đơn mới
        store_id: 1,
        delivery_date: moment().add(6, 'hours'), //add 6 hours
      }}
      onValuesChange={scheduleSaveDraft}
      className="order-container"
    >
      <Row gutter={[16, 16]} style={{ alignItems: 'flex-start' }}>
        <Col xs={24} lg={14} className="tw-h-full">
          <OrderProductItems
            form={form}
            onCalcTotal={() => calTotalAmountRef.current?.()}
            returnOrder={!!record?.order_return_id}
          />
        </Col>
        <Col xs={24} lg={10} className="tw-h-full">
          <OrderCustomerForm
            form={form}
            setCustomer={setCustomer}
            sellerOptions={sellerOptions}
            storeOptions={storeOptions}
            setTotalAmount={setTotalAmountTemp}
            onRegisterCalcFn={(fn) => {
              calTotalAmountRef.current = fn;
            }}
            shipperOptions={shipperOptions}
            designerOptions={designerOptions}
            fileList={fileList}
            uploadProps={props}
            isNewOrder={!record}
            initialCustomer={draftPrefill.customer}
            keepInitialAddress={draftPrefill.keepAddress}
            onCustomerChange={isDraftMode ? (c) => saveDraft(tabKey, { customer: c }) : undefined}
          />
        </Col>
      </Row>

      <div className="order-button">
        <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
          <Button
            type="default"
            size="large"
            onClick={() => handleSubmit('print')}
            icon={<PrinterOutlined />}
            loading={isPrinting}
            disabled={isSubmitting}
            style={{
              background: 'white',
              borderColor: '#d9d9d9',
              color: '#595959',
            }}
          >
            {isPrinting ? 'Đang in...' : 'In đơn hàng'}
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={() => handleSubmit('create_update')}
            icon={<BankOutlined />}
            loading={isSubmitting}
            disabled={isPrinting}
          >
            {isSubmitting
              ? record
                ? 'Đang cập nhật...'
                : 'Đang tạo đơn hàng...'
              : record
              ? 'Cập nhật đơn hàng'
              : 'Tạo đơn hàng'}
          </Button>
          <Button
            danger
            size="large"
            onClick={handleCancel}
            icon={<MdCancel />}
            disabled={isSubmitting || isPrinting}
            style={{
              minWidth: '100px',
            }}
          >
            Hủy
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export default OrderForm;
